import type { IndustryNodeId } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import { LegacyIndustryFilterValue } from "../models/LegacyIndustryFilter/LegacyIndustryFilterValue";
import { SiteTagsFilterValue } from "../models/SiteTagsFilter/SiteTagsFilterValue";
import type { LeadsSearchAllFiltersTypes } from "../types/leadsSearchFilters.dto";

/**
 * Filter values for an advanced-search deep link, built from the raw value a linking surface actually holds
 * — a tag string, a taxonomy node id. The write-side counterpart of `toInitialFilterValues`
 * (`pages/advanced-search/utils/urlSeededFilterValues.ts`), which reads the same values back off the URL.
 * The two cannot share a file: the reader belongs to the page that owns the URL, this belongs to the module
 * that owns the filter models.
 *
 * It exists so a linking surface never learns a filter's value shape. The search request is built from the
 * raw redux value (`selectComposedFiltersValue`) with no normalisation in between, so a deep-linked value
 * must already be canonical — which means knowing that `siteTags` carries an `inclusion` and what its
 * default is. That is this module's knowledge, not a company-analysis page's.
 *
 * Deliberately **not** called a seed. `InitialLeadsSeed`, `seedsDefault` and `SeededDraft` each already mean
 * something else here, and all three are consumer-side: a seed is what a surface *applies*. This produces
 * what a producer *puts in the URL*, which only becomes a seed once the router decodes it and
 * `toInitialFilterValues` whitelists it.
 *
 * Adding an entry is only needed for a surface holding a raw value. The `filtersPayload` slot itself stays
 * open to every registered filter — it is a flat map keyed by filter key — so a caller already holding a
 * filter value can pass it directly without an entry here.
 */
export const urlFilterValues = {
    siteTag: (tag: string) => ({ siteTags: SiteTagsFilterValue.toDtoValue({ values: [tag] }) }),

    industryTaxonomyNode: (id: IndustryNodeId) => ({
        industryTaxonomy: { inclusion: inclusionOptionMap.includeOnly, values: [id] },
    }),

    /**
     * Takes the classification's own display path (`"Main"` or `"Main/Sub"`) — the form every surface holds
     * it in — because splitting it is the value-shape knowledge this module exists to keep. An unsplit path
     * reaches the panel as a `mainCategory` no category id can match, which the legacy filter does not
     * survive.
     *
     * @deprecated Legacy classification (ADR-0013). New links should carry a taxonomy node instead.
     */
    legacyIndustry: (industryPath: string) => {
        const [mainCategory, subCategory] = industryPath.split("/");

        return {
            industryFilter: LegacyIndustryFilterValue.toDtoValue({
                values: [{ mainCategory, ...(subCategory ? { subCategory } : {}) }],
            }),
        };
    },
    // Every entry must return keys the panel→DTO fold can route, or the value is silently dropped there.
    // Caught at compile time rather than discovered as a link that quietly filters nothing.
} satisfies Record<string, (...args: never[]) => Partial<LeadsSearchAllFiltersTypes>>;
